import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Debounce wrapper for an async (network-write) callback.
 *
 * Phase 6 (plan.md H1) — replaces `useDebouncedCallback` for any
 * caller that wraps a mutation. The legacy hook silently dropped
 * the pending timer on unmount: a user who edited a settings field
 * and tapped back within the 1s debounce lost the change with no
 * "Saving…" indicator, no toast, and no recovery path.
 *
 * The new contract:
 *
 *   - `call(...args)` debounces; the trailing args win.
 *   - `flush()` immediately fires whatever is pending and awaits
 *     the underlying promise. Idempotent: extra calls with no
 *     pending work resolve immediately. It returns `false` when
 *     the fired/in-flight work rejects, so navigation guards can
 *     keep the user on screen and show the exposed `error`.
 *     `flush()` is the hook every
 *     screen wires to navigation-`beforeRemove` and to
 *     `AppState.change(state === 'background')` to make sure no
 *     edit is lost.
 *   - `pending` is a reactive boolean that stays `true` from the
 *     moment a debounced call is scheduled until the underlying
 *     promise settles. Bind it to a "Saving…" / "Saved" indicator.
 *   - On unmount the pending timer fires in a microtask; we
 *     deliberately do NOT cancel. React Query's mutation promise
 *     carries the request to completion fire-and-forget. We also
 *     do not `await` it (we're already unmounting), so the cleanup
 *     stays synchronous from React's perspective.
 *
 * The function reference is held in a `ref` so re-renders that
 * pass a new closure don't reset the debounce; only `delayMs`
 * changes do.
 */
export function useDebouncedAsyncCallback<A extends unknown[], R>(
  fn: (...args: A) => Promise<R>,
  delayMs: number,
): {
  call: (...args: A) => void;
  flush: () => Promise<boolean>;
  retry: () => Promise<boolean>;
  pending: boolean;
  error: unknown | null;
  succeededAt: Date | null;
} {
  const fnRef = useRef(fn);
  useEffect(() => {
    fnRef.current = fn;
  }, [fn]);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);
  // Pending args + a resolver so `flush()` (and the implicit
  // unmount-flush) can settle the promise the caller awaits.
  const pendingArgsRef = useRef<A | null>(null);
  const lastFailedArgsRef = useRef<A | null>(null);
  const inFlightRef = useRef<Promise<unknown> | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<unknown | null>(null);
  const [succeededAt, setSucceededAt] = useState<Date | null>(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const runArgs = useCallback((args: A): Promise<unknown> => {
    let promise: Promise<unknown>;
    try {
      promise = Promise.resolve(fnRef.current(...args));
    } catch (err) {
      promise = Promise.reject(err);
    }
    const observed = promise
      .then((value) => {
        lastFailedArgsRef.current = null;
        if (mountedRef.current) {
          setError(null);
          setSucceededAt(new Date());
        }
        return value;
      })
      .catch((err: unknown) => {
        lastFailedArgsRef.current = args;
        if (mountedRef.current) {
          setError(err);
        }
        throw err;
      })
      .finally(() => {
        if (inFlightRef.current === observed) {
          inFlightRef.current = null;
          if (mountedRef.current) setPending(false);
        }
      });
    inFlightRef.current = observed;
    // We swallow errors here so a rejected mutation doesn't bubble
    // into an unhandled-rejection warning during background flushes.
    // Caller-visible error state is exposed via `error`; mutation
    // hooks can still attach their own telemetry through React Query.
    observed.catch(() => {
      /* see comment above */
    });
    return observed;
  }, []);

  // `runNow` synchronously fires the pending args (if any), tracks
  // the in-flight promise, and toggles `pending`. Returns whatever
  // promise the caller can await — `flush()` exposes this; `call()`
  // uses it from inside `setTimeout`.
  const runNow = useCallback((): Promise<unknown> | null => {
    const args = pendingArgsRef.current;
    if (!args) return null;
    pendingArgsRef.current = null;
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    return runArgs(args);
  }, [runArgs]);

  const call = useCallback(
    (...args: A) => {
      pendingArgsRef.current = args;
      lastFailedArgsRef.current = null;
      setError(null);
      setPending(true);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        runNow();
      }, delayMs);
    },
    [delayMs, runNow],
  );

  const flush = useCallback(async (): Promise<boolean> => {
    const fired = runNow();
    const inFlight = fired ?? inFlightRef.current;
    if (!inFlight) return true;
    try {
      await inFlight;
      return true;
    } catch {
      // See `runNow` comment — caller-visible error handling is
      // exposed via `error`. `flush()` resolves either way so
      // event handlers can await it without try/catch.
      return false;
    }
  }, [runNow]);

  const retry = useCallback(async (): Promise<boolean> => {
    const args = lastFailedArgsRef.current;
    if (!args) return true;
    setError(null);
    setPending(true);
    try {
      await runArgs(args);
      return true;
    } catch {
      // Error state is set by `runArgs`; keep the retry action
      // awaitable without forcing every UI button to catch.
      return false;
    }
  }, [runArgs]);

  useEffect(() => {
    return () => {
      // Don't cancel the pending call on unmount. Schedule it in a
      // microtask so it runs after React has finished tearing down,
      // and let React Query's mutation promise carry the work to
      // completion. We deliberately don't await — we're in a sync
      // cleanup path.
      if (pendingArgsRef.current) {
        queueMicrotask(() => {
          runNow();
        });
      }
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
    // We intentionally use the stable `runNow` ref-style closure;
    // there is no other unmount-time dependency.
  }, [runNow]);

  return { call, flush, retry, pending, error, succeededAt };
}
